/**
 * The letters that ask a customer for the first payment.
 *
 * Mercury used to send this, and as of 2026-09-06 it does not: Jonathon asked
 * for no bank-transfer request to reach the customer at all. Mercury raises an
 * invoice only once somebody has chosen how to pay, and every letter before
 * that comes from here, out of the rep's own mailbox, so the customer is
 * answering a person they have met rather than a bank they have not.
 *
 * The wording is here rather than in `email_templates` on purpose. These two go
 * out with nobody watching, and a row somebody edits by accident is a letter
 * nobody proofreads; a change here is a pull request. Templates remain what a
 * rep picks by hand.
 *
 * Both letters are plain text. `buildRawMessage` makes the HTML part from it,
 * and plain text is what survives every mail client a homeowner might use.
 */

import { cents, formatMoney, subCents, type Cents } from "./money.js";

export interface PaymentLetterInput {
  /** What the customer is called. First name where we have one. */
  customerName?: string | null;
  /** The payment being asked for, before any card fee. */
  due: Cents;
  /** Where the work is, so the letter is obviously about their roof. */
  address?: string | null;
  /** The `/p/<token>` page. */
  link: string;
  /** Who it is from. */
  repName?: string | null;
  /**
   * The rep's own mobile.
   *
   * Jonathon, 2026-09-06: the number in the letter has to be the number of the
   * person who sent it. The letter leaves that rep's mailbox, so a reply goes
   * to them and a call has to reach them too — a customer told to "call us" on
   * a switchboard has been handed off by a letter that reads as personal.
   * Falls back to the company number, and then to nothing at all rather than
   * printing an empty one.
   */
  repPhone?: string | null;
  /** The trading name, out of `company_settings` (CLAUDE.md rule 11). */
  companyName: string;
  /** The number to call when the rep has none on file. */
  companyPhone?: string | null;
  /** Whether the page will be offering financing. */
  financingOffered?: boolean;
  /**
   * Everything this customer still owes, when it is more than `due`.
   *
   * They come apart on one job in ten: a customer whose deposit never arrived
   * owes the deposit AND the balance, while this link can only charge the one
   * slot it was raised for. Jonathon, 2026-09-08: "we don't send an email to a
   * customer that says they only x when they actually owe y."
   *
   * So the letter says both, in words. It does not quietly ask for the larger
   * figure — the link cannot charge it — and it does not print the smaller one
   * alone as though it were the whole debt. Null or equal to `due` on the
   * ordinary job, where the sentence is left out entirely.
   *
   * Always `jobBalance().outstanding` from packages/core. Never a subtraction
   * done at the call site.
   */
  outstanding?: Cents | null | undefined;
  /**
   * Money the customer has already sent that has not reached the bank yet.
   *
   * A card deposit sits at Stripe for two or three days. The balance counts
   * only money that has landed (ADR 0139), so without this the letter would
   * name it as still outstanding and ask a customer for a payment they made on
   * Friday. Jonathon, 2026-09-13, asked what it should say: that it is on its
   * way, and don't ask for it.
   *
   * Comes off the "owed in total" sentence and is named on its own line.
   */
  onItsWay?: Cents | null;
}

export interface PaymentLetter {
  subject: string;
  text: string;
}

/**
 * "Hi Sarah," where we know, "Hello," where we do not.
 *
 * First name only. A letter that opens "Hi Sarah Jenkins-Moore," reads like a
 * bill from somebody who has never met her, which is the opposite of the point.
 */
function greeting(name?: string | null): string {
  const first = (name ?? "").trim().split(/\s+/)[0];
  return first ? `Hi ${first},` : "Hello,";
}

/** " on your roof at 12 Oak Street" — omitted entirely when we have no address. */
function atTheAddress(address?: string | null): string {
  const trimmed = (address ?? "").trim();
  return trimmed ? ` at ${trimmed}` : "";
}

function signOff(input: PaymentLetterInput): string {
  const name = (input.repName ?? "").trim();
  // The rep's own number first. The letter comes from their mailbox and is
  // signed with their name; a different number under it sends the customer to
  // somebody who has never heard of them.
  const phone =
    (input.repPhone ?? "").trim() || (input.companyPhone ?? "").trim();
  const call = phone
    ? `reply to this email or call me on ${phone}`
    : "reply to this email";
  return [
    `If anything looks wrong, or you would rather sort it out over the phone, ${call}.`,
    "",
    "Thank you,",
    ...(name ? [name] : []),
    input.companyName,
  ].join("\n");
}

/** The line about the three choices, which changes when there is no lender yet. */
function theChoices(financingOffered?: boolean): string {
  const base =
    "Bank transfer is free. Credit card payments carry a 3% convenience fee, " +
    "which is shown on the page before you pay anything.";
  return financingOffered
    ? base +
        " There is also an option to apply for financing and spread the cost."
    : base;
}

/**
 * The sentence that lets a customer who has paid off the hook.
 *
 * It used to say "it can take a day or two to reach us", which is true of a
 * card or a bank transfer and not of a check. A customer who had mailed one
 * read a reminder, then another two days later, and rang to ask whether it
 * had been lost (2026-10-04, several of them). So the check is named, and so
 * is how long it takes.
 */
function alreadyPaid(): string {
  return (
    "If you have already paid, or have mailed or handed us a check, please " +
    "ignore this. A card or bank payment can take a day or two to reach us, " +
    "and a check a week or more."
  );
}

/**
 * The letter that goes out the moment the contract is signed.
 *
 * It is the only thing telling the customer the page exists after they close
 * the tab, so the link carries the letter — no attachment, no login, nothing to
 * remember.
 */
export function firstPaymentLetter(input: PaymentLetterInput): PaymentLetter {
  return {
    subject: `Your ${input.companyName} contract is signed — how would you like to pay?`,
    text: [
      greeting(input.customerName),
      "",
      `Thank you for choosing ${input.companyName}. Your contract for the work` +
        `${atTheAddress(input.address)} is signed and we have your copy on file.`,
      "",
      `Your first payment of ${formatMoney(input.due)} is due now. You can make it here:`,
      "",
      input.link,
      "",
      theChoices(input.financingOffered),
      "",
      "We order materials and book your dates once the first payment is in, so " +
        "the sooner it lands the sooner we can get you on the calendar.",
      "",
      signOff(input),
    ].join("\n"),
  };
}

/**
 * The nudge, every other day, until it is paid or a rep calls it off.
 *
 * Deliberately the same link and the same three choices as the first letter.
 * A reminder that introduces a new way to pay is a reminder that reads like a
 * different bill.
 */
export function paymentReminderLetter(
  input: PaymentLetterInput,
): PaymentLetter {
  return {
    subject: `A reminder about your first payment — ${input.companyName}`,
    text: [
      greeting(input.customerName),
      "",
      `This is a friendly reminder that the first payment of ` +
        `${formatMoney(input.due)} for the work${atTheAddress(input.address)} ` +
        "has not come through yet.",
      "",
      "You can make it here:",
      "",
      input.link,
      "",
      theChoices(input.financingOffered),
      "",
      alreadyPaid(),
      "",
      signOff(input),
    ].join("\n"),
  };
}

/**
 * The balance, asked for when the work is finished.
 *
 * A rep reads this one before it goes. Jonathon, 2026-09-06: marking the last
 * work order complete should "pop up an email prompt for the rep that marks the
 * work order complete so they can manually review the email". The job has just
 * been on somebody's roof for two days; whatever needs saying about how it went
 * is said by the person who was there, not by a template.
 *
 * **It never mentions financing** — his instruction, and it is the right one.
 * Financing is arranged before the work, not after it, and offering it beside a
 * finished roof invites a customer to go and apply rather than pay.
 */
export function finalPaymentLetter(input: PaymentLetterInput): PaymentLetter {
  return {
    subject: `Your roof is finished — the balance of ${formatMoney(input.due)}`,
    text: [
      greeting(input.customerName),
      "",
      `The work${atTheAddress(input.address)} is complete. Thank you for ` +
        `trusting ${input.companyName} with it.`,
      "",
      `That leaves a balance of ${formatMoney(input.due)}. You can pay it here:`,
      "",
      input.link,
      "",
      ...stillOwedElsewhere(input),
      // The same two methods as the first payment and no third: financing is
      // arranged before the work, not after it.
      "Bank transfer is free. Credit card payments carry a 3% convenience fee, " +
        "which is shown on the page before you pay anything.",
      "",
      signOff(input),
    ].join("\n"),
  };
}

/**
 * The nudge about the balance, every other day once a rep has sent the
 * balance letter, until the customer starts paying it.
 *
 * Jonathon, 2026-10-03: reminders on the final payment go "once that email is
 * sent until the customer initiates it". The same link, the same two ways to
 * pay and no financing, for the reason the balance letter gives; and the same
 * sentence about anything owed from earlier, so the reminder never asks for
 * less than the letter it follows.
 */
export function balanceReminderLetter(
  input: PaymentLetterInput,
): PaymentLetter {
  return {
    subject: `A reminder about your balance — ${input.companyName}`,
    text: [
      greeting(input.customerName),
      "",
      `This is a friendly reminder that the balance of ` +
        `${formatMoney(input.due)} for the work${atTheAddress(input.address)} ` +
        "has not come through yet.",
      "",
      "You can pay it here:",
      "",
      input.link,
      "",
      ...stillOwedElsewhere(input),
      "Bank transfer is free. Credit card payments carry a 3% convenience fee, " +
        "which is shown on the page before you pay anything.",
      "",
      alreadyPaid(),
      "",
      signOff(input),
    ].join("\n"),
  };
}

/**
 * The sentence for a customer who owes more than this link can charge.
 *
 * An empty list on the ordinary job, which is nearly all of them: the balance
 * is the balance and there is nothing to add. It appears when an earlier
 * payment never arrived, and it names the difference rather than the total, so
 * the two figures in the letter add up in front of the reader instead of
 * contradicting each other.
 */
function stillOwedElsewhere(input: PaymentLetterInput): string[] {
  const outstanding =
    input.outstanding == null ? null : cents(input.outstanding);
  if (outstanding == null) return [];

  // Money already sent is not money owed. It is named, so the customer can see
  // we know about it, and it is not added to the figure they are asked for.
  const travelling = cents(Math.max(0, Number(input.onItsWay ?? 0)));
  const onItsWay =
    travelling > 0
      ? [
          `A payment of ${formatMoney(travelling)} is on its way to our bank ` +
            "and is not included below — there is nothing for you to do about " +
            "that one.",
          "",
        ]
      : [];

  const total = cents(Math.max(0, outstanding - travelling));
  if (total <= cents(input.due)) return onItsWay;

  const earlier = subCents(total, cents(input.due));
  return [
    ...onItsWay,
    `Our records also show ${formatMoney(earlier)} outstanding from earlier in ` +
      `the job, so ${formatMoney(total)} is owed in total. If you have already ` +
      "sent that, please ignore this part — and do tell us, so we can chase it " +
      "at our end rather than yours.",
    "",
  ];
}
